import {Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, distinctUntilChanged, map, Observable} from 'rxjs';

/**
 * MAP-1618: assistant progress is PER SMAP, so "is something running" is not one
 * boolean. The head in the assistant belongs to the smap on screen; the head in
 * the toolbar stands in for work the assistant is not currently showing. Both can
 * run at once when one request belongs to this smap and another to a different one.
 */
export function assistantRunningForMap(
  activeMapIds: readonly string[],
  currentMapId: string | null | undefined
): boolean {
  return !!currentMapId && activeMapIds.includes(currentMapId);
}

export function assistantRunningForOtherMap(
  activeMapIds: readonly string[],
  currentMapId: string | null | undefined
): boolean {
  return activeMapIds.some((id) => id !== currentMapId);
}

/**
 * Closed: the toolbar stands in for every request, wherever it belongs.
 * Open: the assistant already shows this smap's request, so the toolbar only
 * reports the ones it cannot show.
 */
export function toolbarAssistantShouldAnimate(
  activeMapIds: readonly string[],
  currentMapId: string | null | undefined,
  assistantOpen: boolean
): boolean {
  return assistantOpen
    ? assistantRunningForOtherMap(activeMapIds, currentMapId)
    : activeMapIds.length > 0;
}

@Injectable({
  providedIn: 'root'
})
export class GenerationStateService {
  private activeSmapJobs = new Set<string>();
  private smapJobsSubject = new BehaviorSubject<number>(0);

  private activeAssistantJobs = new Map<string, string>();
  private assistantJobsSubject = new BehaviorSubject<number>(0);
  private assistantMapIdsSubject = new BehaviorSubject<string[]>([]);

  private activePropertyJobs = new Set<string>();
  private propertyJobsSubject = new BehaviorSubject<number>(0);

  private assistantOpenSubject = new BehaviorSubject<boolean>(false);

  public smapGenerationInProgress$: Observable<boolean> = this.smapJobsSubject.pipe(
    map(count => count > 0),
    distinctUntilChanged(),
  );

  public assistantInProgress$: Observable<boolean> = this.assistantJobsSubject.pipe(
    map(count => count > 0),
    distinctUntilChanged(),
  );

  public propertyGenerationInProgress$: Observable<boolean> = this.propertyJobsSubject.pipe(
    map(count => count > 0),
    distinctUntilChanged(),
  );

  public assistantOpen$: Observable<boolean> = this.assistantOpenSubject.pipe(distinctUntilChanged());

  /** The smaps that currently have an assistant request in flight. */
  public activeAssistantMapIds$: Observable<string[]> = this.assistantMapIdsSubject.asObservable();

  /**
   * Toolbar assistant icon (MAP-1402, reworked for MAP-1618). While the assistant
   * is open it already shows the current smap's request, so the toolbar reports
   * only requests belonging to OTHER smaps; while it is closed the toolbar
   * reports everything.
   */
  toolbarAssistantAnimating$(currentMapId$: Observable<string | null | undefined>): Observable<boolean> {
    return combineLatest([
      this.activeAssistantMapIds$,
      currentMapId$,
      this.assistantOpen$,
    ]).pipe(
      map(([ids, currentMapId, open]) => toolbarAssistantShouldAnimate(ids, currentMapId, open)),
      distinctUntilChanged(),
    );
  }

  startSmapGeneration(jobId: string): void {
    if (!jobId || this.activeSmapJobs.has(jobId)) return;
    this.activeSmapJobs.add(jobId);
    this.smapJobsSubject.next(this.activeSmapJobs.size);
  }

  endSmapGeneration(jobId: string): void {
    if (!jobId || !this.activeSmapJobs.has(jobId)) return;
    this.activeSmapJobs.delete(jobId);
    this.smapJobsSubject.next(this.activeSmapJobs.size);
  }

  startAssistantTask(taskId: string, mapId: string): void {
    if (!taskId || this.activeAssistantJobs.has(taskId)) return;
    this.activeAssistantJobs.set(taskId, mapId);
    this.publishAssistantJobs();
  }

  endAssistantTask(taskId: string): void {
    if (!taskId || !this.activeAssistantJobs.has(taskId)) return;
    this.activeAssistantJobs.delete(taskId);
    this.publishAssistantJobs();
  }

  /** True while a request started on this smap is still running. */
  isAssistantRunningForMap(mapId: string | null | undefined): boolean {
    return assistantRunningForMap(this.assistantMapIdsSubject.value, mapId);
  }

  private publishAssistantJobs(): void {
    this.assistantMapIdsSubject.next([...this.activeAssistantJobs.values()]);
    this.assistantJobsSubject.next(this.activeAssistantJobs.size);
  }

  /**
   * Find an active assistant job that originated on the given smap. Used on
   * assistant reopen (MAP-1443) to re-attach to a stream that started before
   * the panel was closed, so the progress bubble and the auto-apply contract
   * survive a close/reopen cycle.
   */
  getActiveAssistantJobIdForMap(mapId: string): string | null {
    if (!mapId) return null;
    for (const [jobId, jobMapId] of this.activeAssistantJobs) {
      if (jobMapId === mapId) return jobId;
    }
    return null;
  }

  startPropertyGeneration(taskId: string): void {
    if (!taskId || this.activePropertyJobs.has(taskId)) return;
    this.activePropertyJobs.add(taskId);
    this.propertyJobsSubject.next(this.activePropertyJobs.size);
  }

  endPropertyGeneration(taskId: string): void {
    if (!taskId || !this.activePropertyJobs.has(taskId)) return;
    this.activePropertyJobs.delete(taskId);
    this.propertyJobsSubject.next(this.activePropertyJobs.size);
  }

  setAssistantOpen(open: boolean): void {
    this.assistantOpenSubject.next(open);
  }
}
